import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection, query, where, getDocs, getDoc, updateDoc, addDoc, deleteDoc, doc,
  serverTimestamp, orderBy, onSnapshot,
} from 'firebase/firestore';
import {
  Box, Typography, Button, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Avatar, Chip, IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// eslint-disable-next-line no-unused-vars
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import OfferPreview from '../components/OfferPreview';

/* ─── Bullet-aware content renderer ─────────────────────────────────────── */
function renderContent(text) {
  if (!text) return null;
  const parts = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) { parts.push({ type: 'bullets', items: [...bullets] }); bullets = []; }
  };
  text.split('\n').forEach(line => {
    const m = line.match(/^[-•]\s+(.*)/);
    if (m) bullets.push(m[1]);
    else { flush(); parts.push({ type: 'line', text: line }); }
  });
  flush();
  return (
    <Box>
      {parts.map((p, i) => {
        if (p.type === 'bullets') return (
          <Box key={i} component="ul" sx={{ m: 0, pl: 3, mb: 0.5 }}>
            {p.items.map((item, j) => (
              <Typography key={j} component="li" sx={{ fontSize: '11pt', color: '#333', lineHeight: 1.7 }}>
                {item}
              </Typography>
            ))}
          </Box>
        );
        if (!p.text.trim()) return <Box key={i} sx={{ height: '0.5em' }} />;
        return <Typography key={i} sx={{ fontSize: '11pt', color: '#333', lineHeight: 1.7 }}>{p.text}</Typography>;
      })}
    </Box>
  );
}

/* ─── Intro text renderer (matches OfferPreview logic) ──────────────────── */
function renderIntroText(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <Box sx={{ mb: 3 }}>
      {lines.reduce((acc, line, i, arr) => {
        if (line.startsWith('•')) {
          acc.push(
            <Box key={i} sx={{ display: 'flex', gap: 0.8, mb: 0.2 }}>
              <Typography sx={{ fontSize: '11pt', color: '#FF6B00', lineHeight: 1.6, flexShrink: 0 }}>•</Typography>
              <Typography sx={{ fontSize: '11pt', color: '#333', lineHeight: 1.6 }}>
                {line.replace(/^•\s*/, '')}
              </Typography>
            </Box>
          );
        } else if (line.trim() === '') {
          acc.push(<Box key={i} sx={{ mb: 0.8 }} />);
        } else {
          const isHeading = line.length < 80 && !line.endsWith('.') && !line.endsWith(',') &&
            i < arr.length - 1 && arr[i + 1].trim() !== '' && !arr[i + 1].startsWith('•');
          acc.push(
            <Typography key={i} sx={{
              fontSize: '11pt',
              fontWeight: isHeading ? 700 : 400,
              color: isHeading ? '#1a1a1a' : '#333',
              lineHeight: 1.65, mb: isHeading ? 0.2 : 0,
            }}>
              {line}
            </Typography>
          );
        }
        return acc;
      }, [])}
    </Box>
  );
}

/* ─── Pricing block ──────────────────────────────────────────────────────── */
function PricingBlock({ section }) {
  const isHourly = section.pricingMode !== 'fixed';
  const rows = (section.rows || []).filter(r => r.description);
  return (
    <Box>
      <Typography sx={{ fontSize: '11pt', color: '#444', mb: 1.5 }}>
        {isHourly ? 'Ersättning enligt löpande räkning mot budget:' : 'Vi erbjuder oss att utföra uppdraget mot fast pris:'}
      </Typography>
      <Box sx={{ border: '1px solid #e8e8e8', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
        {rows.map((row, i) => (
          <Box key={row.id} sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, py: 1.25,
            bgcolor: i % 2 === 0 ? 'white' : '#fafafa',
            borderBottom: i < rows.length - 1 ? '1px solid #f0f0f0' : 'none',
          }}>
            <Box>
              <Typography sx={{ fontSize: '11pt', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                {row.description}
              </Typography>
              {row.note && <Typography sx={{ fontSize: '9pt', color: '#888' }}>{row.note}</Typography>}
            </Box>
            <Typography sx={{ fontSize: '11pt', fontWeight: 700, ml: 3, whiteSpace: 'nowrap' }}>
              {row.price ? `${Number(row.price).toLocaleString('sv-SE')} SEK${isHourly ? '/tim' : ''}` : '–'}
            </Typography>
          </Box>
        ))}
      </Box>
      {isHourly && section.budgetNote && (
        <Typography sx={{ fontSize: '10pt', fontWeight: 700, color: '#222', mb: 0.5 }}>
          Budget: <span style={{ fontWeight: 400 }}>{section.budgetNote}</span>
        </Typography>
      )}
      {section.paymentTerms && (
        <Typography sx={{ fontSize: '10pt', color: '#555' }}>
          <strong>Betalningsvillkor:</strong> {section.paymentTerms}
        </Typography>
      )}
    </Box>
  );
}

/* ─── Team block ─────────────────────────────────────────────────────────── */
function TeamBlock({ section, users }) {
  const teamUsers = users.filter(u => (section.userIds || []).includes(u.id));
  return (
    <Box>
      {teamUsers.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: section.content ? 2 : 0 }}>
          {teamUsers.map(u => (
            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: '#1a1a1a' }}>
                {(u.displayName || u.name || u.email || '?')[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '11pt', fontWeight: 600 }}>
                  {u.displayName || u.name || u.email}
                </Typography>
                {u.title && <Typography sx={{ fontSize: '9pt', color: '#777' }}>{u.title}</Typography>}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {section.content && renderContent(section.content)}
    </Box>
  );
}

/* ─── Reference projects block ───────────────────────────────────────────── */
function RefsBlock({ companyRefs, selectedRefIds, sectionIndex }) {
  const selected = companyRefs.filter(r => selectedRefIds.includes(r.id));
  if (!selected.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '11pt', color: '#FF6B00', mb: 0.5, letterSpacing: 0.3 }}>
        {String(sectionIndex).padStart(2, '0')} | REFERENSUPPDRAG
      </Typography>
      <Divider sx={{ mb: 2, borderColor: '#FF6B00', borderWidth: 1.5, width: 40 }} />
      {selected.map((ref, i) => (
        <Box key={ref.id} sx={{
          display: 'flex', gap: 2, mb: 2.5, pb: 2.5,
          borderBottom: i < selected.length - 1 ? '1px solid #eee' : 'none',
        }}>
          {ref.image ? (
            <Box component="img" src={ref.image}
              sx={{ width: 100, height: 75, flexShrink: 0, objectFit: 'cover', borderRadius: 1 }} />
          ) : (
            <Box sx={{
              width: 100, height: 75, flexShrink: 0, bgcolor: '#f0f0f0',
              borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: '7pt', color: '#bbb' }}>BILD</Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '11pt' }}>{ref.name}</Typography>
              {ref.projectNumber && (
                <Typography sx={{ fontSize: '9pt', color: '#999' }}>#{ref.projectNumber}</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
              {ref.client && <Typography sx={{ fontSize: '9pt', color: '#555' }}>{ref.client}</Typography>}
              {(ref.startDate || ref.endDate) && (
                <Typography sx={{ fontSize: '9pt', color: '#888' }}>
                  {ref.startDate}{ref.endDate ? ' – ' + ref.endDate : ''}
                </Typography>
              )}
            </Box>
            {ref.projectDescription && (
              <Typography sx={{ fontSize: '10pt', color: '#333', lineHeight: 1.6, mb: 0.3 }}>
                {ref.projectDescription}
              </Typography>
            )}
            {ref.ourAssignment && (
              <Typography sx={{ fontSize: '10pt', color: '#555', lineHeight: 1.6 }}>
                <strong>Vår uppgift: </strong>{ref.ourAssignment}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */
export default function PublicOfferView() {
  const { shareToken } = useParams();
  const [offer, setOffer] = useState(null);
  const [offerId, setOfferId] = useState(null);
  const [users, setUsers] = useState([]);
  const [companyRefs, setCompanyRefs] = useState([]);
  const [company, setCompany] = useState(null);
  const [logos, setLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [commentDialog, setCommentDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [senderName, setSenderName] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [approved, setApproved] = useState(false);
  const viewDocRef = useRef(null);
  const viewStartTime = useRef(Date.now());
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        // Load offer by share token
        const q = query(
          collection(db, 'offers'),
          where('shareToken', '==', shareToken),
          where('shareEnabled', '==', true),
        );
        const snap = await getDocs(q);
        if (snap.empty) { setNotFound(true); setLoading(false); return; }
        const d = snap.docs[0];
        const offerData = { id: d.id, ...d.data() };
        setOfferId(d.id);
        setOffer(offerData);
        setApproved(offerData.clientResponse?.status === 'approved');
        setSenderName(offerData.clientContact || '');

        // Log this view
        try {
          const vRef = await addDoc(collection(db, 'offerViews'), {
            offerId: d.id,
            openedAt: serverTimestamp(),
            sessionId: Math.random().toString(36).slice(2),
            duration: 0,
          });
          viewDocRef.current = vRef;
        } catch (_) {}

        // Load supporting data in parallel
        const [userSnap, refSnap, orgSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(query(collection(db, 'referenceProjects'), orderBy('name'))),
          getDocs(collection(db, 'companies')),
        ]);
        setUsers(userSnap.docs.map(d2 => ({ id: d2.id, ...d2.data() })));
        setCompanyRefs(refSnap.docs.map(d2 => ({ id: d2.id, ...d2.data() })));
        if (!orgSnap.empty) {
          const org = orgSnap.docs[0].data();
          setLogos({ logo: org.logo || '', certLogo: org.certLogo || '', name: org.name || '' });
        }
        if (offerData.companyId) {
          const compSnap = await getDoc(doc(db, 'crmCompanies', offerData.companyId));
          if (compSnap.exists()) setCompany({ id: compSnap.id, ...compSnap.data() });
        }
      } catch (_) {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [shareToken]);

  // Duration tracking — update the view doc when the user leaves
  useEffect(() => {
    const flush = () => {
      if (!viewDocRef.current) return;
      const secs = Math.round((Date.now() - viewStartTime.current) / 1000);
      try { updateDoc(viewDocRef.current, { duration: secs }); } catch (_) {}
    };
    const handleVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      flush();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flush);
    };
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: offer?.title || offer?.offerNumber || 'Offert',
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 20mm 0 10mm 0;
        @bottom-center {
          content: "Tikab Strukturmekanik AB | Björnstigen 87 | 170 73 Solna | +46 8 409 043 00 | info@tikab.com | Org.nr: 556246-6275";
          font-size: 7pt;
          color: #888;
          font-family: "Segoe UI", Arial, sans-serif;
        }
      }
      @page :first {
        margin-top: 0;
        margin-bottom: 0;
        @bottom-center { content: none; }
      }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #f0f0f0 !important; }
    `,
  });

  // Real-time comment thread
  useEffect(() => {
    if (!offerId) return;
    const unsub = onSnapshot(
      query(collection(db, 'offers', offerId, 'comments'), orderBy('createdAt', 'asc')),
      snap => setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [offerId]);

  const openResponse = (status) => { setPendingStatus(status); setCommentDialog(true); };

  const submitResponse = async () => {
    setSaving(true);
    const update = {
      'clientResponse.status': pendingStatus,
      'clientResponse.comment': comment.trim(),
      'clientResponse.respondedAt': serverTimestamp(),
    };
    if (pendingStatus === 'approved') update.status = 'accepted';
    await updateDoc(doc(db, 'offers', offerId), update);
    // Also post to comment thread if there's text
    if (comment.trim()) {
      await addDoc(collection(db, 'offers', offerId, 'comments'), {
        text: comment.trim(),
        side: 'client',
        authorName: senderName.trim() || offer?.clientContact || 'Kund',
        createdAt: serverTimestamp(),
      });
    }
    setSaving(false);
    setCommentDialog(false);
    if (pendingStatus === 'approved') setApproved(true);
  };

  const sendComment = async () => {
    if (!commentText.trim() || !offerId) return;
    setSendingComment(true);
    await addDoc(collection(db, 'offers', offerId, 'comments'), {
      text: commentText.trim(),
      side: 'client',
      authorName: senderName.trim() || offer?.clientContact || 'Kund',
      createdAt: serverTimestamp(),
    });
    // Mark offer as having a comment if not already responded
    if (!approved) {
      await updateDoc(doc(db, 'offers', offerId), {
        'clientResponse.status': 'commented',
        'clientResponse.respondedAt': serverTimestamp(),
      });
    }
    setCommentText('');
    setSendingComment(false);
  };

  const deleteComment = async (commentId) => {
    await deleteDoc(doc(db, 'offers', offerId, 'comments', commentId));
  };

  /* ── Loading / not found ── */
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ color: '#FF6B00' }} />
    </Box>
  );
  if (notFound) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', gap: 2, px: 3, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight={700}>Offerten hittades inte</Typography>
      <Typography color="text.secondary">Länken är ogiltig eller har inaktiverats av avsändaren.</Typography>
    </Box>
  );

  /* ── Main offer view ── */
  const sections = offer.sections || [];
  const selectedRefIds = offer.selectedRefIds || [];
  const today = new Date().toLocaleDateString('sv-SE');

  return (
    <Box sx={{ bgcolor: '#e8e8e8', minHeight: '100vh', pb: 4 }}>

      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        {logos.logo
          ? <Box component="img" src={logos.logo} sx={{ height: 32, objectFit: 'contain' }} />
          : <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.1rem', letterSpacing: -0.5 }}>
              {logos.name || 'Tikab'}
            </Typography>}
        <Box sx={{ flex: 1 }} />
        {offer.offerNumber && (
          <Chip label={offer.offerNumber} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '0.75rem' }} />
        )}
        <Button size="small" variant="contained" startIcon={<PrintIcon />}
          onClick={handlePrint}
          className="no-print"
          sx={{ bgcolor: 'white', color: '#1a1a1a', '&:hover': { bgcolor: '#e0e0e0' }, ml: 1 }}>
          Spara PDF
        </Button>
      </Box>

      {/* Two-column layout: offer + comments (stacks on mobile) */}
      <Box sx={{ maxWidth: 1280, mx: 'auto', mt: 3, mb: 3, px: 2,
        display: 'flex', gap: 3, alignItems: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' } }}>

      {/* A4-style page */}
      <Box sx={{
        flex: 1, minWidth: 0,
        bgcolor: 'white', boxShadow: 4, borderRadius: 1,
        display: 'flex', overflow: 'hidden',
        fontFamily: '"Segoe UI", Arial, sans-serif',
      }}>

        {/* ── Sidebar ── */}
        <Box sx={{
          width: { xs: 130, sm: 200 }, minWidth: { xs: 130, sm: 200 }, flexShrink: 0,
          bgcolor: '#f0f0f0', borderRight: '1px solid #ddd',
          p: { xs: '24px 12px 20px', sm: '40px 18px 30px' },
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Company logo */}
          <Box sx={{ mb: 3 }}>
            {logos.logo
              ? <Box component="img" src={logos.logo}
                  sx={{ maxWidth: 140, maxHeight: 55, objectFit: 'contain', display: 'block' }} />
              : <Typography sx={{ fontWeight: 900, fontSize: '20pt', color: '#1a1a1a', letterSpacing: -1, lineHeight: 1 }}>
                  {logos.name || 'tikab'}
                </Typography>}
          </Box>

          {[
            { label: 'DATUM', value: today },
            { label: 'OFFERTNUMMER', value: offer.offerNumber || '–' },
            { label: 'VÅR PART', value: [logos.name || 'Tikab Strukturmekanik AB', offer.ourContact, offer.ourEmail, offer.ourPhone].filter(Boolean).join('\n') || '–' },
            { label: 'ER PART', value: [company?.name, offer.clientContact, offer.clientEmail, offer.clientPhone].filter(Boolean).join('\n') || '–' },
            ...(offer.validUntil ? [{ label: 'GILTIG T.O.M.', value: offer.validUntil }] : []),
          ].map((item, i) => (
            <Box key={i} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: '8pt', fontWeight: 900, color: '#FF6B00', letterSpacing: 0.8, textTransform: 'uppercase', mb: 0.4 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: '9pt', color: '#333', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                {item.value}
              </Typography>
            </Box>
          ))}

          {logos.certLogo && (
            <Box sx={{ mt: 2 }}>
              <Box component="img" src={logos.certLogo}
                sx={{ width: 80, maxHeight: 60, objectFit: 'contain', display: 'block' }} />
            </Box>
          )}

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 'auto' }} />
        </Box>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, p: { xs: '20px 16px 30px', sm: '40px 45px 50px 40px' } }}>
          {/* OFFERT heading */}
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '22pt', sm: '34pt' }, color: '#FF6B00', lineHeight: 1, mb: 3, letterSpacing: -1 }}>
            OFFERT
          </Typography>

          {/* Title + subtitle */}
          <Typography sx={{ fontWeight: 700, fontSize: '13pt', color: '#1a1a1a', mb: offer.subtitle ? 0.3 : 1.5, lineHeight: 1.3 }}>
            {offer.title || offer.meta?.title || 'Offert'}
          </Typography>
          {(offer.subtitle || offer.meta?.subtitle) && (
            <Typography sx={{ fontWeight: 600, fontSize: '13pt', color: '#FF6B00', mb: 2 }}>
              {offer.subtitle || offer.meta?.subtitle}
            </Typography>
          )}

          {/* Intro / säljande text */}
          {offer.introText && renderIntroText(offer.introText)}

          {/* Sections */}
          {sections.map((sec, idx) => {
            if (sec.type === 'pagebreak') return <Box key={sec.id} sx={{ breakBefore: 'page', '@media screen': { display: 'none' } }} />;
            return (
            <Box key={sec.id} sx={{ mb: 3.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '11pt', color: '#FF6B00', mb: 1, letterSpacing: 0.3 }}>
                {String(idx + 1).padStart(2, '0')} | {sec.title.toUpperCase()}
              </Typography>
              {sec.type === 'pricing' ? (
                <PricingBlock section={sec} />
              ) : sec.type === 'team' ? (
                <TeamBlock section={sec} users={users} />
              ) : (
                <>
                  {sec.content && (
                    <Box sx={{ mb: (sec.images || []).length > 0 ? 1.5 : 0 }}>
                      {renderContent(sec.content)}
                    </Box>
                  )}
                  {(sec.images || []).length > 0 && (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: sec.imageLayout === '1col' ? '1fr' : '1fr 1fr',
                      gap: '8px', mt: 1,
                    }}>
                      {sec.images.map(img => (
                        <Box key={img.id} component="img" src={img.url}
                          sx={{ width: '100%', display: 'block', borderRadius: '2px' }} />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
            );
          })}

          {/* Reference projects */}
          {selectedRefIds.length > 0 && (
            <RefsBlock
              companyRefs={companyRefs}
              selectedRefIds={selectedRefIds}
              sectionIndex={sections.length + 1}
            />
          )}

          {/* Attachments */}
          {(offer.attachments || []).length > 0 && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '10pt', color: '#FF6B00', mb: 1.5, letterSpacing: 0.3 }}>
                BILAGOR
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {offer.attachments.map(att => (
                  <Box key={att.id} component="a" href={att.url} target="_blank" rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25,
                      border: '1px solid #e8e8e8', borderRadius: 1, bgcolor: '#fafafa',
                      textDecoration: 'none', color: 'inherit',
                      '&:hover': { borderColor: '#FF6B00', bgcolor: '#FFF8F0' },
                      transition: 'all 0.15s',
                    }}>
                    <InsertDriveFileIcon sx={{ color: '#FF6B00', fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '10pt', color: '#1a1a1a', flex: 1 }}>{att.name}</Typography>
                    <Typography sx={{ fontSize: '8pt', color: '#aaa' }}>Öppna ↗</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '8pt', color: '#aaa', textAlign: 'center' }}>
              Tikab Strukturmekanik AB | Björnstigen 87 | 170 73 Solna | +46 8 409 043 00 | info@tikab.com | Org.nr: 556246-6275
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Sticky comments sidebar ── */}
      <Box sx={{
        width: { xs: '100%', md: 320 }, flexShrink: 0,
        position: { xs: 'static', md: 'sticky' }, top: 16,
        maxHeight: { xs: 'none', md: 'calc(100vh - 48px)' },
        display: 'flex', flexDirection: 'column',
        bgcolor: 'white', boxShadow: 4, borderRadius: 1,
        fontFamily: '"Segoe UI", Arial, sans-serif',
        overflow: 'hidden',
      }}>
        {/* Approval button */}
        <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
          {approved ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8,
              bgcolor: '#E8F5E9', px: 2, py: 1, borderRadius: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#2E7D32' }}>
                Offert godkänd
              </Typography>
            </Box>
          ) : (
            <Button fullWidth variant="contained" startIcon={<CheckCircleIcon />}
              onClick={() => openResponse('approved')}
              sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Godkänn offert
            </Button>
          )}
        </Box>

        {/* Thread header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography fontWeight={700} sx={{ fontSize: '0.8rem', color: '#555',
            textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Kommentarer {comments.length > 0 && `(${comments.length})`}
          </Typography>
        </Box>

        {/* Thread */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {comments.length === 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
              Inga kommentarer ännu.
            </Typography>
          )}
          {comments.map(c => {
            const isClient = c.side === 'client';
            return (
              <Box key={c.id}>
                <Box sx={{
                  bgcolor: isClient ? '#EFF6FF' : '#FFF7ED',
                  border: `1px solid ${isClient ? '#BFDBFE' : '#FED7AA'}`,
                  borderRadius: isClient ? '4px 10px 10px 10px' : '10px 4px 10px 10px',
                  p: '7px 10px',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700,
                      color: isClient ? '#1e40af' : '#c2410c', flex: 1 }}>
                      {c.authorName || (isClient ? 'Kund' : 'Tikab')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#bbb' }}>
                      {c.createdAt?.toDate?.()?.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) || ''}
                    </Typography>
                    {isClient && (
                      <IconButton size="small" onClick={() => deleteComment(c.id)}
                        sx={{ p: 0.15, opacity: 0.3, '&:hover': { opacity: 1, color: 'error.main' } }}>
                        <DeleteIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: '#1a1a1a', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {c.text}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Input area */}
        <Box sx={{ p: 1.5, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField size="small" placeholder="Ditt namn (valfritt)"
            value={senderName} onChange={e => setSenderName(e.target.value)}
            InputProps={{ sx: { fontSize: '0.75rem' } }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField fullWidth multiline minRows={2} maxRows={5}
              placeholder="Skriv en kommentar... (Ctrl+Enter)"
              value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendComment(); }}
              InputProps={{ sx: { fontSize: '0.8rem' } }} />
            <Button variant="contained" onClick={sendComment}
              disabled={!commentText.trim() || sendingComment}
              sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' },
                minWidth: 36, px: 1, alignSelf: 'flex-end' }}>
              <SendIcon sx={{ fontSize: 16 }} />
            </Button>
          </Box>
        </Box>
      </Box>

      </Box> {/* closes two-column container */}

      {/* Print-only: high-quality OfferPreview matching editor print output */}
      <Box className="print-only">
        <Box ref={printRef}>
          <OfferPreview printMode
            meta={{
              title: offer.title || offer.meta?.title || '',
              subtitle: offer.subtitle || offer.meta?.subtitle || '',
              offerNumber: offer.offerNumber || '',
              ourContact: offer.ourContact || '',
              ourEmail: offer.ourEmail || '',
              ourPhone: offer.ourPhone || '',
              clientContact: offer.clientContact || '',
              clientEmail: offer.clientEmail || '',
              clientPhone: offer.clientPhone || '',
              introText: offer.introText || '',
              validUntil: offer.validUntil || '',
            }}
            sections={sections}
            company={company}
            users={users}
            logos={logos}
            companyRefs={companyRefs}
            selectedRefIds={selectedRefIds}
            budgets={offer?.budgets || []}
          />
        </Box>
      </Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          html, body { margin: 0 !important; padding: 0 !important; background: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Response dialog */}
      <Dialog open={commentDialog} onClose={() => setCommentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingStatus === 'approved' ? 'Godkänn offert' : 'Lämna synpunkter'}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {pendingStatus === 'approved'
              ? 'Du kan lämna en valfri kommentar. Vi återkommer med bekräftelse.'
              : 'Beskriv dina synpunkter eller frågor så återkommer vi.'}
          </Typography>
          <TextField fullWidth multiline rows={4}
            label="Kommentar (valfritt)"
            placeholder={pendingStatus === 'approved'
              ? 'T.ex. önskat startdatum, kontaktperson, övrigt...'
              : 'Vad vill du ändra eller fråga om?'}
            value={comment}
            onChange={e => setComment(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCommentDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={submitResponse} disabled={saving}
            sx={{
              bgcolor: pendingStatus === 'approved' ? '#2E7D32' : '#1565C0',
              '&:hover': { bgcolor: pendingStatus === 'approved' ? '#1b5e20' : '#0d47a1' },
            }}>
            {saving ? 'Skickar...' : pendingStatus === 'approved' ? 'Bekräfta godkännande' : 'Skicka synpunkter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
